import { useMemo } from "react";
import { isEmptyObject } from "../global/function";
import type { Bill } from "../queries/bill.queries";

export type BillStatus = "canceled" | "ird-failed" | "credit-issued" | "normal";

// Single source of truth for the canceled/ird-failed/credit-issued/normal
// branching that invoice-bill.page.jsx used to re-derive four separate times
// (once each in _renderBillView/_renderBillViewButtons for the inline panel,
// and _renderSideModalContent/_renderSideModalHeaderButtons for the modal) —
// a state-handling change previously had to be made in all four places.
// Priority order mirrors the original checks exactly: a canceled bill is
// reported "canceled" even if it also happens to be ird_bill !== "success" or
// bill_void, and "ird-failed" takes priority over "credit-issued" for the
// same reason (an unsynced bill can't have had a credit note successfully
// filed against it yet in practice, but the original code checked in this
// order, so this preserves it).
export function useBillStatus(
	bill: Pick<Bill, "bill_canceled" | "ird_bill" | "bill_void"> | Record<string, never> | undefined,
): BillStatus | null {
	return useMemo(() => {
		if (!bill || isEmptyObject(bill)) return null;
		if (bill.bill_canceled) return "canceled";
		if (bill.ird_bill !== "success") return "ird-failed";
		if (bill.bill_void) return "credit-issued";
		return "normal";
	}, [bill]);
}
